import * as React from "react";
import { Loader2, Globe } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
} from "../../../components/ui-v2/card";
import { Text, MonoText } from "../../../components/ui-v2/typography";
import { API_COLORS, SUCCESS_COLORS, formatMs } from "./constants";

function ApiBarChart({ externalApis }) {
  if (!externalApis) return null;

  const data = Object.entries(externalApis).map(([name, stats]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    calls: stats.totalCalls,
    successRate: parseFloat(stats.successRate) || 0,
    avgLatency: stats.avgLatencyMs,
  }));

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis dataKey="name" tick={{ fontSize: 12 }} className="text-muted-foreground" />
        <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" />
        <Tooltip
          contentStyle={{
            backgroundColor: "hsl(var(--background))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "8px",
            fontSize: "12px",
          }}
        />
        <Bar dataKey="calls" name="Total Calls" fill={API_COLORS.finnhub} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ApiSuccessChart({ externalApis }) {
  if (!externalApis) return null;

  const totalSuccess = Object.values(externalApis).reduce((sum, s) => sum + s.successCalls, 0);
  const totalFailure = Object.values(externalApis).reduce((sum, s) => sum + s.failureCalls, 0);

  if (totalSuccess + totalFailure === 0) {
    return (
      <div className="h-[200px] flex items-center justify-center">
        <Text variant="secondary" size="sm">No API calls recorded</Text>
      </div>
    );
  }

  const data = [
    { name: "Success", value: totalSuccess, color: SUCCESS_COLORS.success },
    { name: "Failure", value: totalFailure, color: SUCCESS_COLORS.failure },
  ].filter(d => d.value > 0);

  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          outerRadius={70}
          innerRadius={40}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function ExternalApisSection({ loading, externalApis }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Globe className="h-4 w-4 text-primary" />
            External API Calls
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          {loading ? (
            <div className="h-[200px] flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <ApiBarChart externalApis={externalApis} />
              {externalApis && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  {Object.entries(externalApis).map(([name, stats]) => (
                    <div key={name} className="text-xs space-y-1 p-2 rounded-md bg-muted/50">
                      <div className="font-medium capitalize">{name}</div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Success Rate</span>
                        <MonoText size="xs">{stats.successRate}</MonoText>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Avg Latency</span>
                        <MonoText size="xs">{formatMs(stats.avgLatencyMs)}</MonoText>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total</span>
                        <MonoText size="xs">{stats.totalCalls}</MonoText>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Globe className="h-4 w-4 text-primary" />
            API Success / Failure
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          {loading ? (
            <div className="h-[200px] flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <ApiSuccessChart externalApis={externalApis} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ExternalApisSection;
